// src/store/index.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { shopAPI } from '../services/api';
import { PACKING_CHARGE_PCT, PACKING_CHARGE_LABEL } from '../config/tenant';

// Price a line item should be charged at:
// the store-wide sale price if one is running, else the normal price.
const unitPriceOf = (item) =>
  (item.has_offer && item.offer_price != null)
    ? parseFloat(item.offer_price)
    : parseFloat(item.selling_price || 0);

// ── Cart Store ─────────────────────────────────────────────────
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      coupon: null,          // checkout coupon (is_global = 0)
      couponDiscount: 0,

      addItem: (product, qty = 1) => {
        const items  = get().items;
        const exists = items.find(i => i.id === product.id);
        // Stock guard (stock_qty from the DB; unknown stock = allowed)
        const stock = product?.stock_qty === null || product?.stock_qty === undefined || product?.stock_qty === ''
          ? null : parseFloat(product.stock_qty);
        if (stock !== null && !Number.isNaN(stock)) {
          if (stock <= 0) return { error: `${product.name} is out of stock` };
          const want = (exists?.qty || 0) + qty;
          if (want > stock) return { error: `Only ${Math.floor(stock)} available for ${product.name}` };
        }
        if (exists) {
          set({ items: items.map(i => i.id === product.id ? { ...i, qty: i.qty + qty } : i) });
        } else {
          // product already carries has_offer / offer_price from the API
          set({ items: [...items, { ...product, qty }] });
        }
        return { success: true };
      },

      removeItem: (id) => set(s => ({ items: s.items.filter(i => i.id !== id) })),

      updateQty: (id, qty) => {
        if (qty <= 0) { set(s => ({ items: s.items.filter(i => i.id !== id) })); return { success: true }; }
        const item  = get().items.find(i => i.id === id);
        const stock = item && item.stock_qty !== null && item.stock_qty !== undefined && item.stock_qty !== ''
          ? parseFloat(item.stock_qty) : null;
        if (stock !== null && !Number.isNaN(stock) && qty > stock) {
          const max = Math.max(0, Math.floor(stock));
          set(s => ({ items: s.items.map(i => i.id === id ? { ...i, qty: max } : i) }));
          return { error: `Only ${max} available` };
        }
        set(s => ({ items: s.items.map(i => i.id === id ? { ...i, qty } : i) }));
        return { success: true };
      },

      clearCart:    () => set({ items: [], coupon: null, couponDiscount: 0 }),
      applyCoupon:  (coupon, discount) => set({ coupon, couponDiscount: discount }),
      removeCoupon: () => set({ coupon: null, couponDiscount: 0 }),

      // ── Helpers used by the UI ──────────────────────────────
      getUnitPrice: (item) => unitPriceOf(item),

      // Saving from the store-wide sale, per line
      getLineSaleSaving: (item) => {
        if (!item.has_offer || item.offer_price == null) return 0;
        const list  = parseFloat(item.selling_price || 0);
        const offer = parseFloat(item.offer_price);
        return parseFloat(((list - offer) * item.qty).toFixed(2));
      },

      // ── Totals ────────────────────────────────────────────
      getCount: () => get().items.reduce((s, i) => s + i.qty, 0),

      // Subtotal already reflects the store-wide sale price
      getSubtotal: () => get().items.reduce((s, i) => s + unitPriceOf(i) * i.qty, 0),

      // What the subtotal would have been at full price
      getListSubtotal: () => get().items.reduce(
        (s, i) => s + parseFloat(i.selling_price || 0) * i.qty, 0),

      // GST is not shown to or charged from the customer on this storefront;
      // getTax() is kept only in case something upstream still needs the
      // figure, but it is no longer added into getTotal().
      getTax: () => get().items.reduce((s, i) => {
        const base = unitPriceOf(i) * i.qty;
        return s + base * (parseFloat(i.tax_rate || 0) / 100);
      }, 0),

      // Total sale discount across the cart
      getSaleDiscount: () => get().getListSubtotal() - get().getSubtotal(),

      getTotal: () => {
        const sub    = get().getSubtotal();
        const coupon = get().coupon;
        let couponOff = 0;
        if (coupon) {
          couponOff = coupon.type === 'percentage'
            ? (sub * coupon.value) / 100
            : coupon.value;
        }
        return Math.round(sub - couponOff);
      },

      // Everything saved: store sale + coupon + MRP
      getTotalSavings: () => {
        const saleSave = get().getSaleDiscount();
        const sub      = get().getSubtotal();
        const coupon   = get().coupon;
        let couponSave = 0;
        if (coupon) {
          couponSave = coupon.type === 'percentage'
            ? (sub * coupon.value) / 100
            : coupon.value;
        }
        return saleSave + couponSave;
      },
    }),
    {
      name: 'shop-cart',
      partialize: s => ({ items: s.items, coupon: s.coupon, couponDiscount: s.couponDiscount }),
    }
  )
);

// ── Auth Store ─────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set) => ({
      customer: null, token: null, isLoggedIn: false,
      login:  (customer, token) => set({ customer, token, isLoggedIn: true }),
      logout: () => { set({ customer:null, token:null, isLoggedIn:false }); localStorage.removeItem('shop-auth'); },
      updateCustomer: (data) => set(s => ({ customer: { ...s.customer, ...data } })),
    }),
    { name: 'shop-auth' }
  )
);

// ── Wishlist Store ─────────────────────────────────────────────
export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [],
      toggle: (product) => {
        const items  = get().items;
        const exists = items.find(i => i.id === product.id);
        set({ items: exists ? items.filter(i => i.id !== product.id) : [...items, product] });
      },
      isWishlisted: (id) => get().items.some(i => i.id === id),
    }),
    { name: 'shop-wishlist' }
  )
);

// ── UI Store ───────────────────────────────────────────────────
export const useUIStore = create((set) => ({
  cartOpen: false, authModal: null, productModal: null, searchOpen: false,
  openCart:     () => set({ cartOpen: true }),
  closeCart:    () => set({ cartOpen: false }),
  openAuth: (m) => set({ authModal: m }),
  closeAuth:    () => set({ authModal: null }),
  openProduct: (p) => set({ productModal: p }),
  closeProduct: () => set({ productModal: null }),
  toggleSearch: () => set(s => ({ searchOpen: !s.searchOpen })),
}));

// ── Shop settings (tenant) ─────────────────────────────────────
// Packing / delivery charge comes from tenants.packing_charge_pct and
// tenants.packing_charge_label (migration 012) via GET /settings.
// Falls back to config/tenant.js if the endpoint isn't there yet.
export const useShopSettings = create((set, get) => ({
  packingPct:   PACKING_CHARGE_PCT,
  packingLabel: PACKING_CHARGE_LABEL,
  loaded: false,
  load: async () => {
    if (get().loaded) return;
    set({ loaded: true });
    try {
      const r = await shopAPI.getSettings();
      const d = r?.data?.data || r?.data || {};
      const pct = parseFloat(d.packing_charge_pct);
      set({
        packingPct:   Number.isNaN(pct) ? get().packingPct : Math.max(0, pct),
        packingLabel: (d.packing_charge_label || '').trim() || get().packingLabel,
      });
    } catch { /* endpoint not available yet — keep the fallback */ }
  },
}));
